/**
 * ================================================================================================
 *  אמצעי תשלום — THE OFFICE'S OWN CARD, AND WHAT WAS CHARGED TO IT
 * ================================================================================================
 *
 * The Owner, 2026-11-01: *"לקוח מזין אמצעי תשלום דרך הממשק המאובטח של קארדקום, גם בהרשמה וגם
 * בהחלפת כרטיס"* and *"החיוב מתבצע במועד ובסכום הנכונים, והתוצאה המאומתת מעדכנת את המנוי ואת מצב
 * התשלום."*
 *
 * ── THERE IS NO CARD FIELD ON THIS PANEL, AND THERE NEVER WILL BE ──────────────────────────────
 *
 * The one control is a button that LEAVES. Pressing it asks the server for a provider-hosted page
 * and sends the browser there, so the number is typed into Cardcom's own page and never into a
 * field this application rendered. `SignupVerifyScreen` makes the same point about the same thing:
 * *"a text box here would invite somebody to paste a card number into the one field on this screen
 * that reaches the server."*
 *
 * ── AND THE CHARGES ARE A SEPARATE READ UNDER A SEPARATE CAPABILITY ────────────────────────────
 *
 * §11: *"צפייה במסמכים, שינוי אמצעי תשלום וביטול הן רשויות נפרדות."* The card is read and written
 * under `billing.payment_method.update`; the charge list is read under `billing.invoice.read`,
 * because what was charged is the same power as seeing the documents and is deliberately not the
 * power to change the instrument. A person holding one and not the other sees the gate's own
 * refusal inside the half they cannot use, which is what `<ServerRead>` renders.
 */

import { useCallback, useState } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { DataTable } from './DataTable';
import { FactList } from './FactList';
import { ServerRead } from './ServerRead';
import { StateBlock } from './StateBlock';
import { StatusChip } from './StatusChip';
import { callOperation } from '../api/operations';
import type { ChargeAttemptView, StoredCardView } from '../api/contract';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { leaveTo } from '../api/leave';
import { useServerData } from '../api/useServerData';

/**
 * THE PROVIDER NAME THAT MEANS "NOBODY CHECKED THIS".
 *
 * `billing.payment_instrument.provider` records who checked the card, and `sandbox` means no
 * provider was asked — the adapter a Staging estate uses accepts a payment method without
 * contacting anybody. An office carrying one is billable and will fail at the first real charge,
 * so the panel says so rather than showing a card that looks ordinary. The same reasoning that put
 * the word in the row instead of leaving it implied.
 */
const UNCHECKED_PROVIDER = 'sandbox';

/** The four charge states the table labels. Keyed off the server's closed vocabulary. */
const CHARGE_STATE_LABEL: Readonly<Record<string, TextKey>> = {
  in_flight: 'card.state.in_flight',
  charged: 'card.state.charged',
  declined: 'card.state.declined',
  unknown: 'card.state.unknown',
};

type Leaving =
  | { readonly kind: 'idle' }
  | { readonly kind: 'asking' }
  | { readonly kind: 'leaving' }
  | { readonly kind: 'refused'; readonly failure: ActionFailure };

export function PaymentMethodPanel(): JSX.Element {
  const { t } = useLocale();
  const readCard = useCallback(() => callOperation('tenant.payment_method.read', {}), []);
  const readCharges = useCallback(() => callOperation('tenant.payment_method.charges', {}), []);
  const card = useServerData(readCard);
  const charges = useServerData(readCharges);
  const [leaving, setLeaving] = useState<Leaving>({ kind: 'idle' });

  /*
   * ── THE ONE WRITE ON THIS PANEL, AND IT ENDS BY LEAVING THE APPLICATION ──────────────────────
   *
   * `asking` while the server talks to the provider, `leaving` once an address comes back. The
   * second state exists because `window.location.assign` is not instantaneous and a button that
   * stays enabled through it invites a second page — which the server would then supersede, so
   * nothing breaks, but the customer would be looking at a page we have already discarded.
   *
   * THE ADDRESS IS THE SERVER'S AND IS NOT BUILT HERE. It came from Cardcom through the route; a
   * URL assembled in the browser would be a URL a browser could choose.
   */
  const goToProvider = useCallback(() => {
    setLeaving({ kind: 'asking' });
    void callOperation('tenant.payment_method.page', { body: {} })
      .then((opened) => {
        setLeaving({ kind: 'leaving' });
        leaveTo(opened.redirectUrl);
      })
      .catch((error: unknown) => {
        setLeaving({ kind: 'refused', failure: classifyActionError(error) });
      });
  }, []);

  return (
    <>
      <ServerRead result={card.result} whatHe={t('card.reading')} skeletonRows={4}>
        {(data) => (
          <section className="gp-panel">
            <h2 className="breakdown__title">{t('card.onFile')}</h2>
            {data.card === null ? (
              <StateBlock titleHe={t('card.none')} bodyHe={t('card.noneBody')} tone="unavailable" />
            ) : (
              <StoredCard card={data.card} />
            )}

            {leaving.kind === 'refused' ? <ActionFailureNotice failure={leaving.failure} /> : null}
            {leaving.kind === 'leaving' ? (
              <p className="form__note" aria-live="polite">
                {t('card.leaving')}
              </p>
            ) : null}

            <div className="actions-row">
              <button
                type="button"
                className="btn btn--primary"
                disabled={leaving.kind === 'asking' || leaving.kind === 'leaving'}
                onClick={goToProvider}
              >
                {data.card === null ? t('card.add') : t('card.replace')}
              </button>
            </div>
          </section>
        )}
      </ServerRead>

      <ServerRead result={charges.result} whatHe={t('card.chargesReading')} skeletonRows={4}>
        {(data) => <ChargeTable charges={data.charges} />}
      </ServerRead>
    </>
  );
}

function StoredCard({ card }: { readonly card: StoredCardView }): JSX.Element {
  const { t } = useLocale();
  /*
   * THE EXPIRY IS TWO FIELDS AND IS RENDERED LTR. A month and a year read `12/2027` in every
   * locale, and a shekel-RTL paragraph would otherwise place them as `2027/12`. The same reason
   * every figure on the market tiles carries `dir="ltr"`.
   */
  const expiry =
    card.cardMonth === null || card.cardYear === null
      ? null
      : `${String(card.cardMonth).padStart(2, '0')}/${String(card.cardYear)}`;
  return (
    <>
      {card.provider === UNCHECKED_PROVIDER ? (
        /*
         * SAID ON THE SCREEN, NOT ONLY IN THE ROW. An office whose card nobody checked is one
         * charge away from a failure nobody expected, and the person who can act on that is
         * reading this panel.
         */
        <StateBlock titleHe={t('card.sandboxNotice')} bodyHe="" tone="unavailable" />
      ) : null}
      <FactList
        facts={[
          { labelHe: t('card.provider'), value: card.provider },
          { labelHe: t('card.brand'), value: card.cardBrand ?? t('card.unknown') },
          {
            labelHe: t('card.last4'),
            /*
             * THE DIRECTION IS ON THE NODE AND NOT ON THE FACT. `FactList` has no `dir` prop, and
             * adding one would put a layout decision in a component that renders label-value pairs
             * for nine other screens. The figure carries its own.
             */
            value:
              card.cardLast4 === null ? (
                t('card.unknown')
              ) : (
                <span dir="ltr">{`•••• ${card.cardLast4}`}</span>
              ),
          },
          {
            labelHe: t('card.expiry'),
            value: expiry === null ? t('card.unknown') : <span dir="ltr">{expiry}</span>,
          },
          {
            labelHe: t('card.tokenExpires'),
            value:
              card.tokenExpiresOn === null ? (
                t('card.unknown')
              ) : (
                <span dir="ltr">{card.tokenExpiresOn}</span>
              ),
          },
        ]}
      />
    </>
  );
}

function ChargeTable({
  charges,
}: {
  readonly charges: readonly ChargeAttemptView[];
}): JSX.Element {
  const { t } = useLocale();
  return (
    <DataTable
      captionHe={t('card.chargesCaption')}
      emptyStateHe={t('card.chargesEmpty')}
      rows={charges}
      rowKey={(row) => row.chargeAttemptUid}
      columns={[
        {
          key: 'period',
          headerHe: t('card.colPeriod'),
          render: (row) => <span dir="ltr">{`${row.periodStart} – ${row.periodEnd}`}</span>,
        },
        {
          key: 'amount',
          headerHe: t('card.colAmount'),
          /*
           * THE SERVER'S DECIMAL STRING AND THE BOOK'S CURRENCY CODE, printed. `DataTable`'s own
           * `formatAmount(number)` is deliberately not used — it takes a JS number, and a price
           * through a number is a rounded price. `TenantBillingScreen`'s header states the rule,
           * and `Amount` is that function's component form.
           */
          render: (row) => <span dir="ltr">{`${row.amountTotal} ${row.currencyCode}`}</span>,
        },
        {
          key: 'state',
          headerHe: t('card.colState'),
          render: (row) => (
            <>
              <StatusChip
                labelHe={t(CHARGE_STATE_LABEL[row.state] ?? 'card.state.unknown')}
                /*
                 * `muted` FOR BOTH UNSETTLED STATES, and the WORD carries the difference — 09
                 * §9.5 A3: colour is never the only carrier of meaning. `card.state.unknown`
                 * reads *"תוצאה לא ידועה — דורש בדיקה"*, which is the sentence that matters.
                 */
                tone={row.state === 'charged' ? 'ok' : row.state === 'declined' ? 'error' : 'muted'}
              />
              {row.alreadyExisted ? <p className="form__note">{t('card.alreadyExisted')}</p> : null}
              {/*
                THE PROVIDER'S OWN REASON, SHOWN. *"אם חסרה הרשאה, הצג זאת במדויק; אם זו תקלה, תקן
                אותה."* A decline the customer cannot read is a decline they cannot act on — and an
                `unknown` outcome's reason is the only thing that tells anybody what to check.
              */}
              {row.failureReason === null ? null : (
                <p className="form__note">{row.failureReason}</p>
              )}
            </>
          ),
        },
        {
          key: 'approval',
          headerHe: t('card.colApproval'),
          render: (row) => <span dir="ltr">{row.providerApproval ?? '—'}</span>,
        },
      ]}
    />
  );
}
